/**
 * OpenAI / ChatGPT Ads conversion tracking.
 *
 * Fires the OpenAI `lead_created` measurement event and mirrors every
 * conversion action into the existing in-house `analytics_events` table.
 * No personal data (name, email, phone, address, message) is ever included.
 */
import { logAnalyticsEvent } from "@/lib/analytics.functions";
import { getAttribution, normalizedSource } from "@/lib/meta-attribution";

declare global {
  interface Window {
    oaiq?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

export type ConversionType = "call_click" | "text_click" | "form_submit";

/** Interactions already counted this page-view, so one click = one event. */
const fired = new Set<string>();

/** Fires the OpenAI lead event. Safe when the pixel never loaded. */
export function fireOpenAiLead(dedupeKey: string): boolean {
  if (typeof window === "undefined") return false;
  if (fired.has(dedupeKey)) return false;
  fired.add(dedupeKey);
  try {
    if (typeof window.oaiq !== "function") return false;
    window.oaiq("measure", "lead_created", { type: "customer_action" });
    return true;
  } catch {
    return false;
  }
}

const SESSION_KEY = "durafoam_anon_session";

/** Stable, anonymous browser identifier — never tied to a person. */
export function anonSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.localStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    window.localStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return "";
  }
}

/**
 * Fires the OpenAI lead event and records the internal conversion row.
 * `dedupeKey` guarantees one physical interaction produces one conversion.
 */
export function trackConversion(
  type: ConversionType,
  opts: { dedupeKey: string; leadId?: string | null } = { dedupeKey: "" },
): boolean {
  if (typeof window === "undefined") return false;
  const key = opts.dedupeKey || `${type}:${Date.now()}`;
  if (fired.has(key)) return false;

  const pixel = fireOpenAiLead(key);

  try {
    const a = getAttribution();
    const first = a.first_touch;
    void logAnalyticsEvent({
      data: {
        event_name: type,
        session_id: anonSessionId(),
        path: window.location.pathname,
        referrer: first?.referrer || "",
        props: {
          event_id: crypto.randomUUID(),
          normalized_source: normalizedSource(a),
          utm_source: a.utm_source || "",
          utm_medium: a.utm_medium || "",
          utm_campaign: a.utm_campaign || "",
          utm_content: a.utm_content || "",
          utm_term: a.utm_term || "",
          first_landing_page: first?.landing_page || a.landing_page || "",
          page_url: window.location.pathname + window.location.search,
          oppref: a.oppref || "",
          lead_id: opts.leadId ?? null,
        },
      },
    }).catch(() => {
      /* fire-and-forget: tracking must never break the page */
    });
  } catch {
    /* ignore */
  }

  return pixel;
}
